import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, X } from "@phosphor-icons/react";
import type { ObservedSpeaker, SessionPanelist } from "../../shared/types.ts";
import { panelistStaleMessage } from "./speakerAliasError.ts";
import { compareObservedSpeakers } from "./sessionSpeakerModel.ts";

export interface AssignSpeakerAliasInput {
  speakerKey: string;
  name: string;
  bio?: string;
}

export interface ReplacePanelistAliasesInput {
  name: string;
  speakerKeys: string[];
}

export interface CreatePanelistForSpeakerInput {
  speakerKey: string;
  speakerLabel: string;
  suggestedName: string;
}

interface SpeakerAliasActions {
  onAssignAlias: (input: AssignSpeakerAliasInput) => Promise<void>;
  onReplacePanelistAliases: (input: ReplacePanelistAliasesInput) => Promise<void>;
}

export function UnassignedSpeakerQueue({
  speakers,
  panelists,
  onAssignAlias,
  onCreatePanelist,
}: {
  speakers: ObservedSpeaker[];
  panelists: SessionPanelist[];
  onAssignAlias: SpeakerAliasActions["onAssignAlias"];
  onCreatePanelist: (input: CreatePanelistForSpeakerInput) => void;
}) {
  const unassigned = useMemo(
    () => speakers.filter((speaker) => !speaker.displayName?.trim()).sort(compareNewestCaptureFirst),
    [speakers],
  );
  const groups = useMemo(() => groupSpeakersByCapture(unassigned), [unassigned]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const activeSpeaker = unassigned.find((speaker) => speaker.speakerKey === activeKey) ?? null;
  const matchingPanelists = useMemo(() => {
    const key = textKey(query);
    return panelists.filter((panelist) =>
      !key || textKey(`${panelist.name} ${panelist.bio}`).includes(key),
    );
  }, [panelists, query]);

  useDismissOnOutsidePointer(rootRef, activeKey !== null, close);

  useEffect(() => {
    if (activeKey && !activeSpeaker) close(false);
  }, [activeKey, activeSpeaker]);

  if (unassigned.length === 0) return null;

  function open(speakerKey: string, trigger: HTMLButtonElement) {
    triggerRef.current = trigger;
    setActiveKey(speakerKey);
    setQuery("");
    setError(null);
  }

  function close(restoreFocus = true) {
    setActiveKey(null);
    setError(null);
    if (restoreFocus) requestAnimationFrame(() => triggerRef.current?.focus());
  }

  async function assign(input: AssignSpeakerAliasInput) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onAssignAlias(input);
      close();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="speaker-unassigned-queue"
      data-testid="speaker-unassigned-queue"
      ref={rootRef}
      onKeyDown={(event) => {
        if (event.key === "Escape" && activeKey) {
          event.preventDefault();
          close();
        }
      }}
    >
      <div className="speaker-alias-queue-head">
        <strong>Unassigned speakers</strong>
        <span>{unassigned.length} new</span>
      </div>
      {groups.map((group) => (
        <div
          className="speaker-alias-capture-group"
          data-testid="speaker-alias-capture-group"
          data-capture-run={group.key}
          key={group.key}
        >
          <div className="speaker-alias-capture-label">{group.label}</div>
          <div className="speaker-alias-pills">
            {group.speakers.map((speaker) => (
              <button
                type="button"
                className="speaker-alias-pill unassigned"
                data-testid="speaker-unassigned-pill"
                data-speaker-key={speaker.speakerKey}
                aria-label={`Assign ${speakerAliasLabel(speaker)}`}
                aria-expanded={activeKey === speaker.speakerKey}
                key={speaker.speakerKey}
                onClick={(event) => open(speaker.speakerKey, event.currentTarget)}
              >
                {speakerAliasLabel(speaker)}
              </button>
            ))}
          </div>
        </div>
      ))}

      {activeSpeaker && (
        <div className="speaker-alias-popover" data-testid="speaker-alias-assign-popover">
          <div className="speaker-alias-popover-head">
            <strong>Assign {speakerAliasLabel(activeSpeaker)}</strong>
            <button type="button" className="icon-btn" aria-label="Close speaker assignment" onClick={() => close()}>
              <X weight="bold" />
            </button>
          </div>
          <input
            autoFocus
            className="speaker-alias-search"
            name="speaker-panelist-search"
            value={query}
            placeholder="Find panelist…"
            aria-label="Find panelist"
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="speaker-alias-option-list">
            {matchingPanelists.map((panelist) => (
              <button
                type="button"
                className="speaker-panelist-option"
                data-testid="speaker-alias-panelist-option"
                disabled={busy}
                key={textKey(panelist.name)}
                onClick={() => void assign({ speakerKey: activeSpeaker.speakerKey, name: panelist.name })}
              >
                <span>
                  <strong>{panelist.name}</strong>
                  {panelist.bio && <small title={panelist.bio}>{panelist.bio}</small>}
                </span>
                <small>{aliasCountLabel(aliasCountForPanelist(speakers, panelist.name))}</small>
              </button>
            ))}
            {matchingPanelists.length === 0 && panelists.length > 0 && (
              <div className="speaker-alias-empty">No matching panelists.</div>
            )}
            <button
              type="button"
              className="speaker-panelist-option create"
              data-testid="speaker-alias-create-option"
              disabled={busy}
              onClick={() => {
                onCreatePanelist({
                  speakerKey: activeSpeaker.speakerKey,
                  speakerLabel: speakerAliasLabel(activeSpeaker),
                  suggestedName: query.trim(),
                });
                close(false);
              }}
            >
              <Plus weight="bold" /> Create new panelist…
            </button>
          </div>
          {error && <div className="speaker-alias-error" role="alert">{error}</div>}
        </div>
      )}
    </div>
  );
}

export function PanelistAliasPicker({
  panelist,
  speakers,
  onReplacePanelistAliases,
}: {
  panelist: SessionPanelist;
  speakers: ObservedSpeaker[];
  onReplacePanelistAliases: SpeakerAliasActions["onReplacePanelistAliases"];
}) {
  const assignedHere = useMemo(
    () => speakers
      .filter((speaker) => sameName(speaker.displayName, panelist.name))
      .sort(compareObservedSpeakers),
    [panelist.name, speakers],
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const filtered = useMemo(() => {
    const key = textKey(query);
    return speakers.filter((speaker) => !key || textKey([
      speakerAliasLabel(speaker),
      speaker.provisionalSpeaker,
      speaker.displayName ?? "",
    ].join(" ")).includes(key));
  }, [query, speakers]);
  const unassigned = filtered.filter((speaker) => !speaker.displayName?.trim()).sort(compareNewestCaptureFirst);
  const current = filtered.filter((speaker) => sameName(speaker.displayName, panelist.name)).sort(compareObservedSpeakers);
  const assignedElsewhere = filtered.filter(
    (speaker) => speaker.displayName?.trim() && !sameName(speaker.displayName, panelist.name),
  ).sort(compareObservedSpeakers);

  useDismissOnOutsidePointer(rootRef, open, close);

  function show() {
    setSelected(new Set(assignedHere.map((speaker) => speaker.speakerKey)));
    setQuery("");
    setError(null);
    setNotice(null);
    setOpen(true);
  }

  function close(restoreFocus = true) {
    setOpen(false);
    setError(null);
    setNotice(null);
    if (restoreFocus) requestAnimationFrame(() => triggerRef.current?.focus());
  }

  function toggle(speakerKey: string) {
    setSelected((currentSelection) => {
      const next = new Set(currentSelection);
      if (next.has(speakerKey)) next.delete(speakerKey);
      else next.add(speakerKey);
      return next;
    });
  }

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onReplacePanelistAliases({ name: panelist.name, speakerKeys: Array.from(selected) });
      close();
    } catch (caught) {
      const staleMessage = panelistStaleMessage(caught);
      if (staleMessage) {
        setOpen(false);
        setQuery("");
        setSelected(new Set());
        setError(null);
        setNotice(staleMessage);
        requestAnimationFrame(() => triggerRef.current?.focus());
      } else {
        setError(errorMessage(caught));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="panelist-alias-picker"
      data-testid="panelist-aliases"
      data-panelist={panelist.name}
      ref={rootRef}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.preventDefault();
          close();
        }
      }}
    >
      <div className="speaker-alias-pills">
        {assignedHere.map((speaker) => (
          <span
            className="speaker-alias-pill assigned"
            data-testid="speaker-assigned-pill"
            data-speaker-key={speaker.speakerKey}
            title={`${speakerAliasLabel(speaker)} — ${panelist.name}`}
            key={speaker.speakerKey}
          >
            {speakerAliasLabel(speaker)}
          </span>
        ))}
        <button
          ref={triggerRef}
          type="button"
          className={assignedHere.length > 0 ? "speaker-alias-add-icon" : "speaker-alias-add-empty"}
          data-testid="speaker-alias-picker-open"
          aria-label={`Assign speaker aliases to ${panelist.name}`}
          aria-expanded={open}
          onClick={() => open ? close(false) : show()}
        >
          <Plus weight="bold" />{assignedHere.length === 0 && <span>Assign speaker</span>}
        </button>
      </div>
      {notice && (
        <div className="speaker-alias-error" role="status" data-testid="speaker-alias-stale-notice">
          {notice}
        </div>
      )}

      {open && (
        <div className="speaker-alias-popover panelist-picker" data-testid="speaker-alias-picker">
          <div className="speaker-alias-popover-head">
            <strong>Speakers for {panelist.name}</strong>
            <button type="button" className="icon-btn" aria-label="Close speaker alias picker" onClick={() => close()}>
              <X weight="bold" />
            </button>
          </div>
          <input
            autoFocus
            className="speaker-alias-search"
            name="speaker-alias-search"
            value={query}
            placeholder="Find speaker alias…"
            aria-label="Find speaker alias"
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="speaker-alias-option-list multiselect">
            <AliasOptionGroup label="Unassigned" speakers={unassigned} selected={selected} onToggle={toggle} />
            <AliasOptionGroup label={`Assigned to ${panelist.name}`} speakers={current} selected={selected} onToggle={toggle} />
            <AliasOptionGroup label="Already assigned" speakers={assignedElsewhere} selected={selected} onToggle={toggle} showAssignee />
            {filtered.length === 0 && <div className="speaker-alias-empty">No matching speaker aliases.</div>}
          </div>
          {error && <div className="speaker-alias-error" role="alert">{error}</div>}
          <div className="speaker-alias-popover-actions">
            <button type="button" className="btn ghost xs" disabled={busy} onClick={() => close()}>
              Cancel
            </button>
            <button type="button" className="btn primary xs" data-testid="speaker-alias-picker-save" disabled={busy} onClick={() => void save()}>
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AliasOptionGroup({
  label,
  speakers,
  selected,
  onToggle,
  showAssignee = false,
}: {
  label: string;
  speakers: ObservedSpeaker[];
  selected: Set<string>;
  onToggle: (speakerKey: string) => void;
  showAssignee?: boolean;
}) {
  if (speakers.length === 0) return null;
  return (
    <div className={showAssignee ? "speaker-alias-option-group demoted" : "speaker-alias-option-group"}>
      <div className="speaker-alias-option-label">{label}</div>
      {speakers.map((speaker) => (
        <label
          className={showAssignee ? "speaker-alias-option with-assignee" : "speaker-alias-option"}
          data-testid="speaker-alias-option"
          key={speaker.speakerKey}
        >
          <input
            type="checkbox"
            name="speaker-aliases"
            checked={selected.has(speaker.speakerKey)}
            onChange={() => onToggle(speaker.speakerKey)}
          />
          <span>{speakerAliasLabel(speaker)}</span>
          {showAssignee && (
            <small className="speaker-alias-assignee" title={speaker.displayName ?? ""}>
              {speaker.displayName}
            </small>
          )}
        </label>
      ))}
    </div>
  );
}

function groupSpeakersByCapture(speakers: ObservedSpeaker[]) {
  const groups = new Map<string, { key: string; label: string; speakers: ObservedSpeaker[] }>();
  for (const speaker of speakers) {
    const runLabel = speaker.capture?.captureRunLabel;
    const key = runLabel ?? "other";
    const existing = groups.get(key);
    if (existing) existing.speakers.push(speaker);
    else groups.set(key, {
      key,
      label: runLabel ? `Capture ${speaker.capture?.captureRunIndex}` : "Other speakers",
      speakers: [speaker],
    });
  }
  return Array.from(groups.values());
}

function compareNewestCaptureFirst(a: ObservedSpeaker, b: ObservedSpeaker): number {
  const aRun = a.capture?.captureRunIndex;
  const bRun = b.capture?.captureRunIndex;
  if (aRun != null && bRun != null && aRun !== bRun) return bRun - aRun;
  if (aRun != null && bRun == null) return -1;
  if (aRun == null && bRun != null) return 1;
  return compareObservedSpeakers(a, b);
}

function speakerAliasLabel(speaker: ObservedSpeaker): string {
  return speaker.capture?.compactLabel ?? speaker.provisionalSpeaker;
}

function aliasCountForPanelist(speakers: ObservedSpeaker[], panelistName: string): number {
  return speakers.filter((speaker) => sameName(speaker.displayName, panelistName)).length;
}

function aliasCountLabel(count: number): string {
  return `${count} alias${count === 1 ? "" : "es"}`;
}

function sameName(left: string | null | undefined, right: string): boolean {
  return textKey(left ?? "") === textKey(right);
}

function textKey(value: string): string {
  return value.trim().toLowerCase();
}

function useDismissOnOutsidePointer(
  rootRef: React.RefObject<HTMLElement | null>,
  open: boolean,
  onClose: (restoreFocus?: boolean) => void,
) {
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current?.contains(event.target as Node)) return;
      onClose(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [onClose, open, rootRef]);
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Could not update speaker aliases.";
}
